<template>
  <div class="pw-donut">
    <div class="pw-donut__chart" :style="{ width: `${size}px`, height: `${size}px` }">
      <svg :width="size" :height="size" role="img" :aria-label="ariaLabel">
        <circle
          v-if="total === 0"
          :cx="size / 2"
          :cy="size / 2"
          :r="(outer + inner) / 2"
          :stroke-width="outer - inner"
          class="pw-donut__track"
          fill="none"
        />
        <path
          v-for="arc in arcs"
          :key="arc.label"
          :d="arc.path"
          :fill="arc.color"
          class="pw-donut__arc"
          :opacity="hover && hover !== arc.label ? 0.35 : 1"
          @mouseenter="hover = arc.label"
          @mouseleave="hover = null"
        />
      </svg>
      <div class="pw-donut__center">
        <div class="pw-donut__value">{{ centerValue }}</div>
        <div class="pw-donut__label">{{ centerLabel }}</div>
      </div>
    </div>

    <ul class="pw-donut__legend">
      <li
        v-for="s in segments"
        :key="s.label"
        :class="{ 'is-dim': hover && hover !== s.label }"
        @mouseenter="hover = s.value ? s.label : null"
        @mouseleave="hover = null"
      >
        <span class="pw-donut__key" :style="{ background: s.color }"></span>
        <span class="pw-donut__name">
          {{ s.label }}
          <span v-if="s.hint" class="pw-donut__hint">{{ s.hint }}</span>
        </span>
        <span class="pw-donut__count">{{ formatValue(s.value) }}</span>
        <span class="pw-donut__pct">{{ pct(s.value) }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
// Part-to-whole ring with a labelled legend (label, count, share) so no
// segment is identified by colour alone.
export default {
  name: "DonutChart",
  props: {
    // [{ label, value, color, hint? }]
    segments: { type: Array, required: true },
    centerValue: { type: [String, Number], default: "" },
    centerLabel: { type: String, default: "" },
    size: { type: Number, default: 168 },
    formatValue: { type: Function, default: (v) => String(v) },
    ariaLabel: { type: String, default: "Donut chart" },
  },
  data() {
    return { hover: null };
  },
  computed: {
    total() {
      return this.segments.reduce((sum, s) => sum + (s.value || 0), 0);
    },
    outer() {
      return this.size / 2 - 2;
    },
    inner() {
      return this.outer - 22;
    },
    arcs() {
      if (!this.total) return [];
      const c = this.size / 2;
      const visible = this.segments.filter((s) => s.value > 0);
      // A 2px surface gap between neighbouring segments
      const gap = visible.length > 1 ? 2 / this.outer : 0;
      let start = -Math.PI / 2;

      return visible.map((s) => {
        const sweep = (s.value / this.total) * Math.PI * 2;
        const a0 = start + gap / 2;
        const a1 = start + sweep - gap / 2;
        start += sweep;
        return { label: s.label, color: s.color, path: this.ring(c, a0, a1) };
      });
    },
  },
  methods: {
    pct(value) {
      if (!this.total) return "—";
      return `${Math.round((value / this.total) * 100)}%`;
    },
    ring(c, a0, a1) {
      // A full circle can't be drawn as one arc; split it in two
      if (a1 - a0 >= Math.PI * 2 - 0.0001) {
        const mid = a0 + Math.PI;
        return this.ring(c, a0, mid) + " " + this.ring(c, mid, a0 + Math.PI * 2);
      }
      const R = this.outer;
      const r = this.inner;
      const large = a1 - a0 > Math.PI ? 1 : 0;
      const p = (rad, a) => `${c + rad * Math.cos(a)},${c + rad * Math.sin(a)}`;
      return `M${p(R, a0)} A${R},${R} 0 ${large} 1 ${p(R, a1)} L${p(r, a1)} A${r},${r} 0 ${large} 0 ${p(r, a0)} Z`;
    },
  },
};
</script>

<style scoped>
.pw-donut {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.pw-donut__chart {
  position: relative;
  flex: 0 0 auto;
}

.pw-donut__chart svg {
  display: block;
}

.pw-donut__track {
  stroke: #eef1f5;
}

.pw-donut__arc {
  transition: opacity 0.15s ease;
  cursor: default;
}

.pw-donut__center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.pw-donut__value {
  font-size: 1.375rem;
  font-weight: 700;
  color: #1a2436;
  line-height: 1.1;
}

.pw-donut__label {
  font-size: 0.75rem;
  color: #66738a;
}

.pw-donut__legend {
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1 1 160px;
  min-width: 160px;
}

.pw-donut__legend li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
  font-size: 0.8125rem;
  color: #1a2436;
  transition: opacity 0.15s ease;
}

.pw-donut__legend li.is-dim {
  opacity: 0.45;
}

.pw-donut__key {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex: 0 0 auto;
}

.pw-donut__name {
  flex: 1 1 auto;
  min-width: 0;
}

.pw-donut__hint {
  display: block;
  font-size: 0.6875rem;
  color: #8d99ad;
  line-height: 1.2;
}

.pw-donut__count {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.pw-donut__pct {
  width: 38px;
  text-align: right;
  color: #66738a;
  font-variant-numeric: tabular-nums;
}
</style>
